import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';

import { type AccountListResponse, getAccounts } from '../../../accounts/api';
import { extractAccountsByIds } from '../../../accounts/queryTransforms';
import { observableToPromise } from '../../observableToPromise';
import type { RequestError } from '../../request';

type SearchInput = { q: string; generation: number };

export function useOmnibarAccountSearch(initialQuery = '') {
  const searchId = useId();
  const rawInput = useRef(initialQuery);
  const [input, setInput] = useState<SearchInput>({ q: initialQuery.trim(), generation: 0 });
  const [committed, setCommitted] = useState<SearchInput>({ q: initialQuery.trim(), generation: 0 });
  const generation = useRef(0);
  const debouncedCommit = useMemo(() => debounce(setCommitted, 250), []);
  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

  const request = useQuery<AccountListResponse, RequestError>({
    queryKey: ['vital', 'omnibar', 'accounts', searchId, committed.generation, committed.q],
    queryFn: ({ signal }) => observableToPromise(getAccounts({ q: committed.q }), signal),
    enabled: committed.q.length >= 2,
    staleTime: 0,
    gcTime: 2 * 60_000,
    retry: (failureCount, error) => ![401, 403, 499].includes(error.status) && failureCount < 2,
  });
  const current = input.generation === committed.generation;
  const eligible = input.q.length >= 2;
  const isLoading = eligible && (!current || (request.data === undefined && (request.isFetching || request.isPending)));
  const unauthorized = request.error !== null && [401, 403, 499].includes(request.error.status);
  const error =
    current && eligible && !request.isFetching && (request.data === undefined || unauthorized) ? request.error : null;
  const accounts = useMemo(
    () => (current && eligible && !isLoading && !error ? extractAccountsByIds(request.data) : []),
    [current, eligible, isLoading, error, request.data],
  );

  const updateInput = (value: string, immediate = false) => {
    const whitespaceOnlyChange = value !== rawInput.current && value.trim() === rawInput.current.trim();
    rawInput.current = value;
    if (!immediate && whitespaceOnlyChange) {
      return;
    }
    debouncedCommit.cancel();
    generation.current += 1;
    const next = { q: value.trim(), generation: generation.current };
    setInput(next);
    if (next.q.length < 2) {
      setCommitted({ ...next, q: '' });
    } else if (immediate) {
      setCommitted(next);
    } else {
      debouncedCommit(next);
    }
  };

  return {
    accounts,
    isLoading,
    error,
    search: (value: string) => updateInput(value),
    reset: () => updateInput('', true),
    retry: () => {
      if (eligible) {
        updateInput(input.q, true);
      }
    },
  };
}
